/**
 * LineChart — line + 10% area wash with a vertical crosshair and one tooltip per X position
 * (spec §6.4, net worth 13 points). Time runs LTR even on an RTL page (spec §6/§8.5), so the
 * SVG carries `dir="ltr"` with Hebrew labels.
 */

import { useState } from 'react';
import { Figure, type TableTwin } from './Figure.tsx';
import { ChartTooltip, useTooltip, type TipContent } from './Tooltip.tsx';
import { areaPath, axisLabel, linePath, ticksBetween } from './chart-utils.ts';
import { useChartWidth } from './useChartWidth.ts';

export interface LinePoint {
  label: string;
  /** Value in agorot. */
  value: number;
  tip: TipContent;
  /** Accessible name of the mark. */
  aria: string;
}

export function LineChart({
  question,
  period,
  summary,
  points,
  step,
  height = 260,
  labelsEvery = 1,
  table,
}: {
  question: string;
  period?: string;
  summary?: string;
  points: LinePoint[];
  /** Y tick step in agorot. */
  step: number;
  height?: number;
  labelsEvery?: number;
  table?: TableTwin;
}) {
  const [ref, width] = useChartWidth(720);
  const tooltip = useTooltip();
  const [active, setActive] = useState<number | null>(null);
  if (points.length < 2) return <div ref={ref} />;

  const ml = 56;
  const mr = 20;
  const mt = 14;
  const mb = 30;
  const values = points.map((p) => p.value);
  const { from, to, ticks } = ticksBetween(Math.min(...values), Math.max(...values), step);
  const px = (index: number) => ml + (index * (width - ml - mr)) / (points.length - 1);
  const py = (value: number) => mt + ((to - value) / (to - from)) * (height - mt - mb);
  const coords = points.map((p, i): [number, number] => [px(i), py(p.value)]);
  const bandWidth = (width - ml - mr) / (points.length - 1);
  const every = Math.max(labelsEvery, width < 480 ? 2 : 1);

  return (
    <div ref={ref}>
      <Figure question={question} period={period} summary={summary} table={table}>
        <svg viewBox={`0 0 ${width} ${height}`}>
          {ticks.map((tick) => (
            <g key={tick}>
              <line className="grid" x1={ml} x2={width - mr} y1={py(tick).toFixed(1)} y2={py(tick).toFixed(1)} />
              <text x={ml - 8} y={(py(tick) + 4).toFixed(1)} textAnchor="end">
                {axisLabel(tick)}
              </text>
            </g>
          ))}
          <line className="axis" x1={ml} x2={width - mr} y1={py(from).toFixed(1)} y2={py(from).toFixed(1)} />
          <path d={areaPath(coords, py(from))} className="s1" style={{ opacity: 0.1 }} />
          <path d={linePath(coords)} fill="none" className="ls1" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
          {points.map((point, index) =>
            index % every === 0 || index === points.length - 1 ? (
              <text key={`x-${point.label}-${index}`} x={px(index).toFixed(1)} y={height - 8} textAnchor="middle">
                {point.label}
              </text>
            ) : null,
          )}
          {points.map((point, index) => {
            const handlers = tooltip.bind(point.tip);
            return (
              <g key={`m-${index}`}>
                <line
                  className={`cross${active === index ? ' on' : ''}`}
                  x1={px(index).toFixed(1)}
                  x2={px(index).toFixed(1)}
                  y1={mt}
                  y2={py(from).toFixed(1)}
                  style={{ opacity: active === index ? 1 : 0 }}
                />
                <circle cx={px(index).toFixed(1)} cy={py(point.value).toFixed(1)} r={index === points.length - 1 ? 5 : 4} className="s1 marker" />
                <rect
                  className="hit"
                  x={(px(index) - bandWidth / 2).toFixed(1)}
                  y={mt}
                  width={bandWidth.toFixed(1)}
                  height={height - mt - mb}
                  tabIndex={0}
                  role="img"
                  aria-label={point.aria}
                  onPointerEnter={(event) => {
                    handlers.onPointerEnter(event);
                    setActive(index);
                  }}
                  onPointerMove={handlers.onPointerMove}
                  onPointerLeave={() => {
                    handlers.onPointerLeave();
                    setActive(null);
                  }}
                  onFocus={(event) => {
                    handlers.onFocus(event);
                    setActive(index);
                  }}
                  onBlur={() => {
                    handlers.onBlur();
                    setActive(null);
                  }}
                />
              </g>
            );
          })}
        </svg>
      </Figure>
      <ChartTooltip state={tooltip.node} />
    </div>
  );
}
